import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { useI18n } from '../i18n';
import './RankedMatchModal.css';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string | null;
  user: { id: string; nickname?: string } | null;
  myRating?: number;
  playerId: string;
}

export function RankedMatchModal({ open, onClose, token, user, myRating = 1000, playerId }: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const socketRef = useRef<Socket | null>(null);
  const matchRoomIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [queueSize, setQueueSize] = useState(0);
  const [opponent, setOpponent] = useState<{ nickname: string; rating: number } | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [countingDown, setCountingDown] = useState(false);

  const nickname = user?.nickname || localStorage.getItem('sudoku_nickname') || t.common.guest;

  const disconnect = () => {
    // tick() zinciri temizlenmezse (ör. handleClose sırasında) sayaç arka
    // planda 0'a inip artık iptal edilmiş/silinmiş bir odaya navigate edebilirdi.
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (socketRef.current) {
      socketRef.current.emit('leave_matchmaking', { player_id: playerId });
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  const handleClose = () => {
    // Rakip bulunup geri sayım başladıysa (oda backend'de zaten oluşmuş) sunucuya
    // açıkça iptal sinyali gönder — aksi halde backend bunu ancak kendi 5sn'lik
    // geri sayımı bitince, disconnect'i dolaylı yoldan fark ederek anlıyordu.
    // Disconnect'ten ÖNCE emit et ki mesaj soket kapanmadan sunucuya ulaşsın.
    if (matchRoomIdRef.current && socketRef.current?.connected) {
      socketRef.current.emit('cancel_ranked_match', { room_id: matchRoomIdRef.current });
    }
    matchRoomIdRef.current = null;
    disconnect();
    setQueueSize(0);
    setOpponent(null);
    setCountingDown(false);
    onClose();
  };

  useEffect(() => {
    if (!open || !user || !token) return;

    const sock = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      sock.emit('join_matchmaking', {
        player_id: playerId,
        user_id: user.id,
        nickname,
        rating: myRating,
      });
    });

    sock.on('matchmaking_joined', (data: { queue_size: number }) => setQueueSize(data.queue_size));
    sock.on('queue_update', (data: { queue_size: number }) => setQueueSize(data.queue_size));

    sock.on('match_found', (data: {
      room_id: string;
      opponent_nickname: string;
      opponent_rating: number;
      countdown?: number;
    }) => {
      setOpponent({ nickname: data.opponent_nickname, rating: data.opponent_rating });
      setCountingDown(true);
      matchRoomIdRef.current = data.room_id;
      let c = data.countdown ?? 5;
      setCountdown(c);
      const tick = () => {
        c--;
        setCountdown(c);
        if (c <= 0) {
          matchRoomIdRef.current = null; // artık iptal edilecek bir countdown yok
          timerRef.current = null;
          localStorage.setItem('sudoku_active_room', JSON.stringify({
            room_id: data.room_id, room_code: '', nickname, player_id: playerId,
          }));
          onClose();
          navigate(`/room/${data.room_id}`, {
            state: { fromMatchmaking: true, opponentNickname: data.opponent_nickname, opponentRating: data.opponent_rating },
          });
          // disconnect: React cleanup'ına bırak — erken disconnect backend'in aktif-sid
          // kontrolünü (5sn sonrası) race condition ile bozuyor; socket RoomPage join_room'u
          // aldıktan sonra zaten geçersiz hale gelir
          sock.disconnect();
          socketRef.current = null;
        } else {
          timerRef.current = setTimeout(tick, 1000);
        }
      };
      timerRef.current = setTimeout(tick, 1000);
    });

    sock.on('matchmaking_timeout', () => handleClose());
    sock.on('match_cancelled', () => {
      // Karşı taraf iptal etti/koptu — backend odayı zaten sildi ve bu oyuncu
      // otomatik olarak kuyruğa geri alınmıyor. "Aranıyor" spinner'ını sahte
      // şekilde göstermeye devam etmek yerine modalı kapat (kullanıcı isterse
      // tekrar "Ranked Oyna"ya basar).
      matchRoomIdRef.current = null;
      handleClose();
    });
    sock.on('error', () => handleClose());

    return () => { disconnect(); };
  }, [open, user]);

  if (!open) return null;

  return (
    <div className="rmm-backdrop" onClick={handleClose}>
      <div className="rmm-card" onClick={e => e.stopPropagation()}>
        <button className="rmm-close" onClick={handleClose}>×</button>

        {!countingDown ? (
          <>
            <div className="rmm-spinner" />
            <h2 className="rmm-title">{t.rankedMatch.searching}</h2>
            <p className="rmm-sub">{t.rankedMatch.searchingSub}</p>
            {queueSize > 1 && <p className="rmm-queue">{t.rankedMatch.queueCount.replace('{count}', String(queueSize))}</p>}
            <button className="rmm-cancel" onClick={handleClose}>{t.common.cancel}</button>
          </>
        ) : (
          <>
            <div className="rmm-found-icon">🎯</div>
            <h2 className="rmm-title rmm-title--found">{t.rankedMatch.found}</h2>
            <div className="rmm-opponent">
              <span className="rmm-avatar">{opponent?.nickname[0].toUpperCase()}</span>
              <span className="rmm-name">{opponent?.nickname}</span>
              <span className="rmm-rating">{opponent?.rating} ELO</span>
            </div>
            <div className="rmm-countdown">{countdown}</div>
            <p className="rmm-sub">{t.rankedMatch.startingIn.replace('{sec}', String(countdown))}</p>
            <button className="rmm-cancel" onClick={handleClose}>{t.common.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
}
